import { useLoaderData, useNavigate } from "react-router";
import type { TestRunResultsLoaderData } from "@/routes/testRunsLoader";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { format } from "date-fns";

interface TestResult {
	id: string;
	test_data_entry_id: string;
	output: string;
	correctness: number | null;
	created_at: string;
	evaluation_status?: string | null;
	evaluated_at?: string | null;
	test_data_entries: {
		input: string;
		expected_output: string;
	};
}

export default function TestRunResults() {
	const data = useLoaderData<TestRunResultsLoaderData>();
	const navigate = useNavigate();
	const [resultsCurrentPage, setResultsCurrentPage] = useState(1);

	const testResults = (data?.testResults ?? []) as TestResult[];
	const resultsTotalCount = data?.totalCount ?? 0;
	const resultsPageSize = data?.pageSize ?? 10;
	const resultsTotalPages = Math.max(
		1,
		Math.ceil(resultsTotalCount / resultsPageSize),
	);
	const stats = data?.stats ?? { correct: 0, partial: 0, incorrect: 0 };
	const deepTestInfo = data?.deepTestInfo;
	const robustnessScore = data?.robustnessScore ?? null;
	const viewedRun = data?.viewedRun;
	const viewedTestSet = data?.viewedTestSet;
	const primaryRun = data?.primaryRun;

	const formatDatasetKind = (kind: string) => {
		if (kind === "behavior_examples") return "Behaviors";
		if (kind === "operational_rules") return "Rules";
		return kind;
	};

	const getCorrectnessColor = (correctness: number) => {
		if (correctness >= 90) return "text-green-600 dark:text-green-400";
		if (correctness >= 75) return "text-yellow-600 dark:text-yellow-400";
		return "text-red-600 dark:text-red-400";
	};

	return (
		<div className="bg-card border border-border rounded-lg">
			<div className="p-6 border-b border-border">
				<div className="flex items-center gap-4 mb-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate(-1)}
						className="p-2"
					>
						<ArrowLeft className="w-4 h-4" />
					</Button>
					<div className="flex-1">
						<h3 className="text-xl font-semibold text-foreground">
							Test Results
						</h3>
					</div>
				</div>

				{/* Dataset and Run Metadata */}
				{deepTestInfo ? (
					// Interleaved results - show both primary and adversarial metadata
					<div className="grid grid-cols-2 gap-4 mb-4">
						<Card className="bg-muted/30">
							<CardContent className="p-4">
								<div className="text-xs font-medium text-muted-foreground mb-2">
									Primary Run
								</div>
								<div className="space-y-1">
									<div className="text-sm font-medium text-foreground">
										{deepTestInfo.primaryTestSet.name}
									</div>
									<div className="text-xs text-muted-foreground">
										{formatDatasetKind(
											deepTestInfo.primaryTestSet.dataset_kind,
										)}
									</div>
									{primaryRun && (
										<div className="text-xs text-muted-foreground">
											{format(new Date(primaryRun.start_time), "PPp")}
										</div>
									)}
								</div>
							</CardContent>
						</Card>
						<Card className="bg-muted/30">
							<CardContent className="p-4">
								<div className="text-xs font-medium text-muted-foreground mb-2">
									Adversarial Run
								</div>
								<div className="space-y-1">
									<div className="text-sm font-medium text-foreground">
										{deepTestInfo.derivativeTestSet.name}
									</div>
									<div className="text-xs text-muted-foreground">
										{formatDatasetKind(
											deepTestInfo.derivativeTestSet.dataset_kind,
										)}
									</div>
									<div className="text-xs text-muted-foreground">
										{format(
											new Date(deepTestInfo.deepTestRun.start_time),
											"PPp",
										)}
									</div>
									{deepTestInfo.derivativeTestSet.creation_method && (
										<div className="text-xs text-muted-foreground">
											Method: {deepTestInfo.derivativeTestSet.creation_method}
										</div>
									)}
								</div>
							</CardContent>
						</Card>
					</div>
				) : (
					// Single run view - show only the viewed run metadata
					viewedTestSet &&
					viewedRun && (
						<Card className="bg-muted/30 mb-4">
							<CardContent className="p-4">
								<div className="space-y-1">
									<div className="text-sm font-medium text-foreground">
										{viewedTestSet.name}
									</div>
									<div className="text-xs text-muted-foreground">
										{formatDatasetKind(viewedTestSet.dataset_kind)}
									</div>
									<div className="text-xs text-muted-foreground">
										{format(new Date(viewedRun.start_time), "PPp")}
									</div>
								</div>
							</CardContent>
						</Card>
					)
				)}

				{/* Stats Section */}
				{testResults.length > 0 && (
					<div className="mt-4 p-4 bg-muted/30 rounded-lg border">
						<h4 className="text-sm font-medium text-foreground mb-3">Stats</h4>
						<div
							className={`grid gap-4 ${
								robustnessScore ? "grid-cols-4" : "grid-cols-3"
							}`}
						>
							<div className="text-center">
								<div className="text-2xl font-bold text-green-600 dark:text-green-400">
									{stats.correct}%
								</div>
								<div className="text-xs text-muted-foreground">Correct</div>
							</div>
							<div className="text-center">
								<div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
									{stats.partial}%
								</div>
								<div className="text-xs text-muted-foreground">Partial</div>
							</div>
							<div className="text-center">
								<div className="text-2xl font-bold text-red-600 dark:text-red-400">
									{stats.incorrect}%
								</div>
								<div className="text-xs text-muted-foreground">Incorrect</div>
							</div>
							{robustnessScore && (
								<div className="text-center">
									<div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
										{Math.round(robustnessScore.robust_accuracy)}%
									</div>
									<div className="text-xs text-muted-foreground">
										Robustness
									</div>
								</div>
							)}
						</div>
					</div>
				)}
			</div>

			<div className="p-6">
				{testResults.length === 0 ? (
					<div className="text-center py-8 text-muted-foreground">
						No test results found.
					</div>
				) : (
					<>
						<div className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead className="w-1/4 min-w-[200px]">Input</TableHead>
										<TableHead className="w-1/4 min-w-[200px]">
											Expected Output
										</TableHead>
										<TableHead className="w-1/4 min-w-[200px]">
											Actual Output
										</TableHead>
										<TableHead className="w-1/4 min-w-[100px]">
											Correctness
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{testResults.map((result) => (
										<TableRow key={result.id}>
											<TableCell className="text-sm align-top">
												<div className="max-w-none break-words whitespace-normal">
													{result.test_data_entries.input}
												</div>
											</TableCell>
											<TableCell className="text-sm align-top">
												<div className="max-w-none break-words whitespace-normal">
													{result.test_data_entries.expected_output}
												</div>
											</TableCell>
											<TableCell className="text-sm align-top">
												<div className="max-w-none break-words whitespace-normal">
													{result.output}
												</div>
											</TableCell>
											<TableCell className="text-sm align-top">
												{result.correctness !== null ? (
													<span
														className={`font-medium ${getCorrectnessColor(result.correctness)}`}
													>
														{result.correctness}%
													</span>
												) : (
													<span className="text-xs text-muted-foreground">
														{result.evaluation_status === "pending"
															? "Pending"
															: result.evaluation_status === "in_progress"
																? "Evaluating..."
																: result.evaluation_status === "failed"
																	? "Failed"
																	: "N/A"}
													</span>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>

						{resultsTotalPages > 1 && (
							<div className="flex items-center justify-between mt-6">
								<div className="text-sm text-muted-foreground">
									Showing {(resultsCurrentPage - 1) * resultsPageSize + 1} to{" "}
									{Math.min(
										resultsCurrentPage * resultsPageSize,
										resultsTotalCount,
									)}{" "}
									of {resultsTotalCount} results
								</div>
								<div className="flex items-center gap-2">
									<Button
										variant="outline"
										size="sm"
										onClick={() =>
											setResultsCurrentPage(Math.max(1, resultsCurrentPage - 1))
										}
										disabled={resultsCurrentPage === 1}
									>
										<ChevronLeft className="w-4 h-4" />
										Previous
									</Button>
									<span className="text-sm">
										Page {resultsCurrentPage} of {resultsTotalPages}
									</span>
									<Button
										variant="outline"
										size="sm"
										onClick={() =>
											setResultsCurrentPage(
												Math.min(resultsTotalPages, resultsCurrentPage + 1),
											)
										}
										disabled={resultsCurrentPage === resultsTotalPages}
									>
										Next
										<ChevronRight className="w-4 h-4" />
									</Button>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
